<script>
    import { onMount, onDestroy } from 'svelte';
    import { fade, fly } from 'svelte/transition';
  
    export let aiAudioElement = null;
  
    let canvas;
    let animationId;
    let audioContext;
    let analyser;
    let dataArray;
    let source;
    let setupComplete = false;
    
    onMount(() => {
      setupAudioVisualization();
    });
  
    onDestroy(() => {
      cleanup();
    });
  
    function cleanup() {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      if (source) {
        try {
          source.disconnect();
        } catch (e) {
          console.log('Error disconnecting source:', e);
        }
      }
      if (analyser) {
        try {
          analyser.disconnect();
        } catch (e) {
          console.log('Error disconnecting analyser:', e);
        }
      }
      if (audioContext) {
        try {
          audioContext.close();
        } catch (e) {
          console.log('Error closing audio context:', e);
        }
      }
    }
  
    function setupAudioVisualization() {
      try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 32;
        
        // Create a gain node to handle connections
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 1;
  
        // Connect the audio element through the gain node
        source = audioContext.createMediaElementSource(aiAudioElement);
        source.connect(gainNode);
        gainNode.connect(analyser);
        gainNode.connect(audioContext.destination);
        
        dataArray = new Uint8Array(analyser.frequencyBinCount);
        setupComplete = true;
        
        animate();
      } catch (error) {
        console.error('Error setting up audio visualization:', error);
        setupComplete = false;
        animateFallback();
      }
    }
  
    function animate() {
      if (!setupComplete) return;
      
      animationId = requestAnimationFrame(animate);
      
      try {
        analyser.getByteFrequencyData(dataArray);
        drawVisualization(dataArray);
      } catch (error) {
        console.error('Error in animation loop:', error);
        setupComplete = false;
        animateFallback();
      }
    }
  
    // Fallback animation when we can't access the audio data
    function animateFallback() {
      let phase = 0;
      
      function animateFrame() {
        animationId = requestAnimationFrame(animateFrame);
        
        // Generate fake data for a smooth animation
        const fakeData = new Uint8Array(16);
        for (let i = 0; i < fakeData.length; i++) {
          fakeData[i] = 128 + Math.sin(phase + i * 0.2) * 128;
        }
        
        drawVisualization(fakeData);
        phase += 0.1;
      }
      
      animateFrame();
    }
  
    function drawVisualization(data) {
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const centerY = canvas.height / 2;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / data.length) * 2.5;
      
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      
      // Draw top curve
      for (let i = 0; i < data.length; i++) {
        const x = i * barWidth;
        const percent = data[i] / 255;
        const height = (canvas.height * 0.7) * percent;
        
        ctx.lineTo(x, centerY + height/2);
      }
      
      // Draw bottom curve
      for (let i = data.length - 1; i >= 0; i--) {
        const x = i * barWidth;
        const percent = data[i] / 255;
        const height = (canvas.height * 0.7) * percent;
        
        ctx.lineTo(x, centerY - height/2);
      }
      
      ctx.closePath();
      
      // Create gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, 'rgba(10, 207, 33, 0.1)');
      gradient.addColorStop(0.5, 'rgba(10, 207, 33, 0.3)');
      gradient.addColorStop(1, 'rgba(10, 207, 33, 0.1)');
      
      ctx.fillStyle = gradient;
      ctx.fill();
      
      ctx.strokeStyle = 'rgba(10, 207, 33, 0.8)';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  </script>
  
  <div 
    class="player-container"
    transition:fly={{ y: 50, duration: 400 }}
  >
    <div class="ai-indicator">🤖 <b>AI Agasthya</b> 👨🏾‍💻</div>
    <canvas
      bind:this={canvas}
      width="300"
      height="80"
    />
  </div>
  
  <style>
    .player-container {
      position: fixed;
      bottom: 2rem;
      left: 50%;
      transform: translateX(-50%);
      background: white;
      padding: 1.5rem;
      border-radius: 1rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      z-index: 1000;
    }
  
    .ai-indicator {
      text-align: center;
      font-family: 'Courier New', Courier, monospace;
      margin-bottom: 1rem;
      font-size: 0.875rem;
      color: #4b5563;
    }
  
    canvas {
      border-radius: 0.5rem;
      background: #f8fafc;
    }
  </style>